import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MessageBody } from "@/components/MessageBody";
import { messagePreview } from "@/lib/messageFormat";
import { ThreadContactPanel } from "@/components/crm/ThreadContactPanel";
import {
  Inbox as InboxIcon, Loader2, Mail, Send, Trash2, Reply, Plus, Building2, Newspaper, User, RefreshCw, Ban, MessageCircle, Clock, Check, CheckCheck, Paperclip, Activity, AlertTriangle, Languages, X, Globe,
} from "lucide-react";

const REPLY_LANGS = [
  { code: "it", label: "Italiano" }, { code: "en", label: "Inglese" }, { code: "de", label: "Tedesco" },
  { code: "fr", label: "Francese" }, { code: "es", label: "Spagnolo" },
];
const langLabel = (code) => REPLY_LANGS.find((l) => l.code === code)?.label || (code || "").toUpperCase();

const AREA = {
  sales: { label: "Commerciale", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300", icon: Building2 },
  media: { label: "Comunicazione", cls: "bg-brand/15 text-brand", icon: Newspaper },
  ambassador: { label: "Marketing", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", icon: User },
};
const CHANNEL = {
  whatsapp: { label: "WhatsApp", cls: "bg-[#25D366]/15 text-[#128C4A] dark:text-[#25D366]", icon: MessageCircle },
  email: { label: "Email", cls: "bg-slate-500/15 text-slate-700 dark:text-slate-300", icon: Mail },
};
const channelOf = (m) => (m?.channel === "whatsapp" ? CHANNEL.whatsapp : CHANNEL.email);
const WA_TICKS = {
  inviata: { icon: Check, cls: "text-muted-foreground", label: "Inviato" },
  consegnata: { icon: CheckCheck, cls: "text-muted-foreground", label: "Consegnato" },
  aperta: { icon: CheckCheck, cls: "text-sky-500", label: "Letto" },
  rimbalzata: { icon: Ban, cls: "text-red-500", label: "Non recapitato" },
};
const waWindowInfo = (thread) => {
  const ins = (thread?.thread || []).filter((t) => t.direction === "in" && t.received_at);
  if (!ins.length) return { state: "waiting", text: "In attesa della prima risposta del rivenditore: finestra 24h non ancora aperta." };
  const last = new Date(ins[ins.length - 1].received_at).getTime();
  const diffH = (Date.now() - last) / 3600000;
  if (diffH >= 24) return { state: "closed", text: "Finestra 24h CHIUSA: per riscrivere serve un template approvato da Meta." };
  const remain = 24 - diffH;
  const h = Math.floor(remain);
  const mm = Math.floor((remain - h) * 60);
  return { state: "open", text: `Finestra 24h aperta · scade tra ${h}h ${mm}m — puoi rispondere a testo libero.` };
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const contactLabel = (t) => ({ lead: "Rivenditore", media: "Testata", ambassador: "Ambassador", user: "Rivenditore B2B", wa_contact: "Contatto WhatsApp" }[t] || "Non associato");
const siteLabel = (u) => (u || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
const siteHref = (u) => (/^https?:\/\//i.test(u || "") ? u : `https://${u}`);

// Testata / Rivenditore / Ambassador di riferimento + sito, ben visibile (lista e modale)
function ContactRef({ type, name, website, size = "sm", testId = "contact-ref" }) {
  if (!name && !website) return null;
  const Icon = type === "lead" || type === "user" ? Building2 : type === "ambassador" ? User : type === "wa_contact" ? MessageCircle : Newspaper;
  const big = size === "lg";
  return (
    <div className={`inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-brand/25 bg-brand/5 ${big ? "px-3 py-1.5 text-sm" : "px-2 py-0.5 text-[11px]"}`}
      data-testid={testId}>
      <span className="inline-flex items-center gap-1 font-semibold text-brand"><Icon className={big ? "h-4 w-4" : "h-3 w-3"} /> {contactLabel(type)}:</span>
      {name && <span className="font-semibold text-foreground" data-testid={`${testId}-name`}>{name}</span>}
      {website && (
        <a href={siteHref(website)} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-site`} onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-brand">
          <Globe className={big ? "h-3.5 w-3.5" : "h-3 w-3"} /> {siteLabel(website)}
        </a>
      )}
    </div>
  );
}

export default function Inbox() {
  const [area, setArea] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyLang, setReplyLang] = useState("en");
  const [replyTranslated, setReplyTranslated] = useState(null);
  const [replyTranslating, setReplyTranslating] = useState(false);
  const replyRef = useRef({ body: "", lang: "en" });
  replyRef.current = { body: replyBody, lang: replyLang };
  const [translations, setTranslations] = useState({});
  const [translatingId, setTranslatingId] = useState(null);
  const [hiddenTr, setHiddenTr] = useState({});

  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ from_email: "", from_name: "", subject: "", body_text: "", area: "sales" });
  const [manualSaving, setManualSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [integ, setInteg] = useState(null);
  const [waDiag, setWaDiag] = useState(null);
  const [waDiagOpen, setWaDiagOpen] = useState(false);
  const loadDiag = () => api.get("/whatsapp/diagnostics").then(({ data }) => setWaDiag(data)).catch(() => {});

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (area !== "all") params.set("area", area);
    if (channelFilter !== "all") params.set("channel", channelFilter);
    if (onlyUnread) params.set("unread", "true");
    api.get(`/inbound?${params.toString()}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 25000); // auto-refresh ogni 25s
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [area, channelFilter, onlyUnread]);
  useEffect(() => {
    api.get("/integrations/status").then(({ data }) => setInteg(data)).catch(() => {});
    loadDiag();
  }, []);

  const openThread = async (m) => {
    setActive(m); setThread(null); setThreadLoading(true); setReplyBody(""); setReplyTranslated(null); setHiddenTr({});
    try {
      const { data } = await api.get(`/inbound/${m._id}/thread`);
      setThread(data);
      setReplyLang(data.suggested_lang || "en");
      const cached = {};
      (data.thread || []).forEach((t) => { if (t.translation_it?.text) cached[t._id] = t.translation_it; });
      setTranslations((prev) => ({ ...prev, ...cached }));
      if (!m.read) { await api.patch(`/inbound/${m._id}`, { read: true }); load(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setThreadLoading(false); }
  };

  // Ricarica il thread dopo un cambio di contatto (associa / scollega / crea scheda / modifica) senza chiudere la modale
  const refreshThread = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/inbound/${active._id}/thread`);
      setThread(data);
      load();
    } catch { /* il thread resta visibile con i dati precedenti */ }
  };

  const translateIncoming = async (t) => {
    setTranslatingId(t._id);
    try {
      const { data } = await api.post(`/inbound/${t._id}/translate`);
      setTranslations((prev) => ({ ...prev, [t._id]: data }));
      setHiddenTr((prev) => ({ ...prev, [t._id]: false }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Traduzione fallita"); }
    finally { setTranslatingId(null); }
  };

  const translateReply = async () => {
    if (!replyBody.trim()) return;
    const src = replyBody; const lang = replyLang;
    setReplyTranslating(true);
    try {
      const { data } = await api.post("/translate-message", { text: src, target_lang: lang });
      if (replyRef.current.body === src && replyRef.current.lang === lang) setReplyTranslated(data.text || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Traduzione fallita"); }
    finally { setReplyTranslating(false); }
  };

  const sendReply = async () => {
    const outgoing = replyTranslated !== null ? replyTranslated : replyBody;
    if (!active || !outgoing.trim()) return;
    setReplySending(true);
    try {
      const { data } = await api.post(`/inbound/${active._id}/reply`, { body: outgoing });
      toast.success(data.sent ? data.note : (data.note || "Risposta salvata"));
      setReplyBody(""); setReplyTranslated(null);
      const { data: td } = await api.get(`/inbound/${active._id}/thread`);
      setThread(td);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
    finally { setReplySending(false); }
  };

  const deleteMsg = async (m, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await api.delete(`/inbound/${m._id}`);
      toast.success("Messaggio eliminato");
      if (active?._id === m._id) setActive(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const saveManual = async () => {
    if (!manual.from_email.trim()) { toast.error("Inserisci l'email del mittente"); return; }
    setManualSaving(true);
    try {
      await api.post("/inbound", manual);
      toast.success("Risposta registrata e contatto aggiornato");
      setManualOpen(false);
      setManual({ from_email: "", from_name: "", subject: "", body_text: "", area: "sales" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setManualSaving(false); }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  const syncImap = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/inbound/imap-sync");
      toast.success(`Sincronizzazione completata: ${data.fetched || 0} nuovi messaggi`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sincronizzazione non riuscita");
    } finally { setSyncing(false); }
  };

  return (
    <div data-testid="inbox-page">
      <PageHeader
        eyebrow="Inbound & Tracking"
        title="Messaggi / Inbox"
        subtitle="Le risposte di rivenditori e giornalisti confluiscono qui: aggiornano lo stato del contatto e permettono di proseguire la conversazione."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" data-testid="wa-diagnostics-btn" onClick={() => { setWaDiagOpen((v) => !v); loadDiag(); }}
              className="rounded-full border border-border bg-transparent font-medium text-foreground hover:bg-brema-hover">
              <Activity className="mr-2 h-4 w-4" /> Diagnostica WhatsApp
            </Button>
            <Button variant="secondary" data-testid="inbox-sync-btn" onClick={syncImap} disabled={syncing}
              className="rounded-full border border-border bg-transparent font-medium text-foreground hover:bg-brema-hover">
              {syncing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sincronizzo…</> : <><RefreshCw className="mr-2 h-4 w-4" /> Sincronizza posta</>}
            </Button>
            <Button data-testid="inbox-manual-btn" onClick={() => setManualOpen(true)}
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" /> Registra risposta
            </Button>
          </div>
        }
      />

      {waDiagOpen && waDiag && (
        <div className="mb-4 rounded-2xl border border-border bg-brema-card p-5" data-testid="wa-diagnostics-panel">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <MessageCircle className="h-4 w-4 text-[#25D366]" />
            <h3 className="text-sm font-semibold text-foreground">Diagnostica ricezione WhatsApp</h3>
            <span className="text-xs text-muted-foreground">· stato del flusso in entrata su questo ambiente</span>
            <Button size="sm" variant="secondary" data-testid="wa-diagnostics-refresh" onClick={loadDiag}
              className="ml-auto h-8 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Aggiorna
            </Button>
          </div>
          {waDiag.webhook_events_total === 0 ? (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground" data-testid="wa-diag-no-events">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <div>
                <p className="font-medium">Meta non ha ancora inviato eventi a questo ambiente.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  In Meta → WhatsApp → Configuration verifica che: (1) il <strong>Callback URL</strong> punti a QUESTO dominio
                  (<code className="rounded bg-muted px-1">{window.location.origin}/api/webhooks/whatsapp</code>);
                  (2) sotto <strong>Webhook fields</strong> il campo <strong>“messages”</strong> sia sottoscritto (Manage → spunta su <em>messages</em>).
                  Un numero WhatsApp invia gli eventi a un solo Callback URL alla volta.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-xl border border-positive/40 bg-positive/10 p-3 text-sm text-foreground" data-testid="wa-diag-ok">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-positive" />
              <div>
                <p className="font-medium">Meta sta inviando eventi a questo ambiente.</p>
                <p className="mt-1 text-xs text-muted-foreground">Ultimo evento: {waDiag.last_event_at ? fmt(waDiag.last_event_at) : "—"}</p>
              </div>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-border bg-brema-bg p-3" data-testid="wa-diag-events-total">
              <div className="text-xs text-muted-foreground">Eventi webhook</div>
              <div className="text-xl font-bold text-foreground">{waDiag.webhook_events_total}</div>
            </div>
            <div className="rounded-xl border border-border bg-brema-bg p-3" data-testid="wa-diag-inbound-total">
              <div className="text-xs text-muted-foreground">Messaggi ricevuti</div>
              <div className="text-xl font-bold text-foreground">{waDiag.inbound_total}</div>
            </div>
            <div className="rounded-xl border border-border bg-brema-bg p-3" data-testid="wa-diag-outbound-total">
              <div className="text-xs text-muted-foreground">Messaggi inviati</div>
              <div className="text-xl font-bold text-foreground">{waDiag.outbound_total}</div>
            </div>
            <div className="rounded-xl border border-border bg-brema-bg p-3" data-testid="wa-diag-enabled">
              <div className="text-xs text-muted-foreground">API WhatsApp</div>
              <div className={`text-sm font-semibold ${waDiag.whatsapp_enabled ? "text-positive" : "text-amber-500"}`}>{waDiag.whatsapp_enabled ? "Attiva" : "Non configurata"}</div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={area} onValueChange={setArea}>
            <TabsList className="bg-brema-card">
              <TabsTrigger value="all" data-testid="inbox-filter-all">Tutti</TabsTrigger>
              <TabsTrigger value="sales" data-testid="inbox-filter-sales">Commerciale</TabsTrigger>
              <TabsTrigger value="media" data-testid="inbox-filter-media">Comunicazione</TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={channelFilter} onValueChange={setChannelFilter}>
            <TabsList className="bg-brema-card">
              <TabsTrigger value="all" data-testid="inbox-channel-all">Tutti i canali</TabsTrigger>
              <TabsTrigger value="email" data-testid="inbox-channel-email">Solo Email</TabsTrigger>
              <TabsTrigger value="whatsapp" data-testid="inbox-channel-whatsapp">Solo WhatsApp</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366]/15 px-2.5 py-1 text-xs font-medium text-[#128C4A] dark:text-[#25D366]" data-testid="inbox-wa-sender">
            <MessageCircle className="h-3.5 w-3.5" /> Invii WhatsApp da: {integ?.whatsapp_sender_number || (integ?.whatsapp ? "numero attivo" : "non ancora configurato")}
          </span>
          <span className="text-sm text-muted-foreground">{unreadCount} non letti</span>
          <Button variant="secondary" size="sm" data-testid="inbox-unread-toggle" onClick={() => setOnlyUnread((v) => !v)}
            className={`rounded-full border border-border text-xs ${onlyUnread ? "bg-selected text-selected-foreground hover:bg-selected/90" : "bg-transparent text-foreground hover:bg-brema-hover"}`}>
            Solo non letti
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : messages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="inbox-empty">
          <InboxIcon className="mx-auto mb-3 h-8 w-8 opacity-50" />
          Nessun messaggio in arrivo. Quando i contatti rispondono alle e-mail (o registri una risposta manualmente) comparirà qui.
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((m) => {
            const a = AREA[m.area] || AREA.sales;
            const AIcon = a.icon;
            return (
              <div key={m._id} data-testid="inbox-message-row" onClick={() => openThread(m)}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors hover:border-primary/40 ${m.read ? "border-border bg-brema-card" : "border-primary/30 bg-brema-card"}`}>
                {!m.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" data-testid="inbox-unread-dot" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-foreground">{m.from_name || m.from_email || m.from_phone}</span>
                    {(() => { const c = channelOf(m); const CIcon = c.icon; return (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${c.cls}`} data-testid="inbox-channel-badge"><CIcon className="h-2.5 w-2.5" /> {c.label}</span>
                    ); })()}
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${a.cls}`}><AIcon className="h-2.5 w-2.5" /> {a.label}</span>
                    {m.opt_out && <span data-testid="inbox-optout-badge" className="inline-flex items-center gap-1 rounded-full border border-red-600/50 bg-red-600/20 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300"><Ban className="h-2.5 w-2.5" /> Opt-out · Non contattare</span>}
                  </div>
                  {(m.contact_name || m.contact_website) && (
                    <div className="mt-1"><ContactRef type={m.contact_type} name={m.contact_name} website={m.contact_website} testId="inbox-row-contact" /></div>
                  )}
                  <p className="mt-0.5 truncate text-sm font-medium text-foreground">{m.subject || (m.channel === "whatsapp" ? "Conversazione WhatsApp" : "(nessun oggetto)")}</p>
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground" data-testid="inbox-row-preview">{messagePreview(m.body_text)}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="text-[11px] text-muted-foreground">{fmt(m.received_at)}</span>
                  <button data-testid="inbox-delete-btn" onClick={(e) => deleteMsg(m, e)} className="text-muted-foreground hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Thread / conversation dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="inbox-thread-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{active?.subject || "Conversazione"}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span>{active?.from_name || active?.from_email || active?.from_phone}</span>
              {(() => { const c = channelOf(active); const CIcon = c.icon; return (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.cls}`} data-testid="thread-channel-badge"><CIcon className="h-3 w-3" /> {c.label}</span>
              ); })()}
              {active?.opt_out && <span data-testid="thread-optout-badge" className="inline-flex items-center gap-1 rounded-full border border-red-600/50 bg-red-600/20 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300"><Ban className="h-3 w-3" /> Opt-out · Non contattare</span>}
            </DialogDescription>
            {thread && (thread.contact_name || thread.contact_website ? (
              <ContactRef type={thread.contact_type} name={thread.contact_name} website={thread.contact_website} size="lg" testId="thread-contact" />
            ) : active?.channel !== "whatsapp" ? (
              <span className="inline-flex w-fit items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground" data-testid="thread-contact-none">
                <AlertTriangle className="h-3 w-3" /> Nessuna testata o contatto associato a questo mittente
              </span>
            ) : null)}
          </DialogHeader>
          {thread && active?.channel === "whatsapp" && (
            <ThreadContactPanel thread={thread} msgId={active._id} onChanged={refreshThread} />
          )}
          {threadLoading || !thread ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div className="max-h-[46vh] space-y-3 overflow-y-auto pr-1" data-testid="inbox-thread-messages">
                {thread.thread.map((t) => {
                  const out = t.direction === "out";
                  return (
                    <div key={t._id} data-testid="inbox-thread-msg"
                      className={`rounded-lg border p-3 ${out ? "border-primary/30 bg-primary/5 ml-6" : "border-border bg-brema-bg mr-6"}`}>
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                        <span className={`font-medium ${out ? "text-brand" : "text-foreground"}`}>{out ? `${t.from_name} (noi)` : (t.from_name || t.from_email || t.from_phone)}</span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          {out && t.channel === "whatsapp" && (() => { const tk = WA_TICKS[t.wa_status || "inviata"] || WA_TICKS.inviata; const TIcon = tk.icon; return (
                            <span className={`inline-flex items-center gap-0.5 ${tk.cls}`} data-testid="wa-delivery-ticks" title={tk.label}><TIcon className="h-3.5 w-3.5" /> {tk.label}</span>
                          ); })()}
                          {fmt(t.received_at)}
                        </span>
                      </div>
                      {t.subject && <p className="text-xs font-medium text-muted-foreground">{t.subject}</p>}
                      <MessageBody text={t.body_text} className="mt-1" testId={out ? "thread-msg-out" : "thread-msg-in"} />
                      {!out && (() => {
                        const tr = translations[t._id];
                        const hidden = !!hiddenTr[t._id];
                        return (
                          <div className="mt-2">
                            {!tr ? (
                              <Button type="button" variant="outline" size="sm" onClick={() => translateIncoming(t)} disabled={translatingId === t._id}
                                className="h-7 rounded-full px-2.5 text-[11px]" data-testid="thread-msg-translate-btn">
                                {translatingId === t._id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Languages className="mr-1 h-3 w-3" />} Traduci in italiano
                              </Button>
                            ) : (
                              <div className="rounded-md border border-brand/30 bg-brand/5 p-2.5" data-testid="thread-msg-translation">
                                <div className="flex items-center justify-between gap-2 text-[11px]">
                                  <span className="inline-flex items-center gap-1 font-semibold text-brand"><Languages className="h-3 w-3" /> Traduzione in italiano{tr.source_lang ? ` · da ${langLabel(tr.source_lang)}` : ""}</span>
                                  <button type="button" onClick={() => setHiddenTr((p) => ({ ...p, [t._id]: !hidden }))} className="text-muted-foreground hover:text-foreground" data-testid="thread-msg-translation-toggle">
                                    {hidden ? "Mostra traduzione" : "Nascondi traduzione"}
                                  </button>
                                </div>
                                {!hidden && <p className="mt-1.5 whitespace-pre-line text-sm text-foreground" data-testid="thread-msg-translation-text">{tr.text}</p>}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {t.wa_document?.filename && <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#128C4A] dark:text-[#25D366]"><Paperclip className="h-3 w-3" /> {t.wa_document.filename}</p>}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-2 border-t border-border pt-3">
                {active?.channel === "whatsapp" && (() => {
                  const w = waWindowInfo(thread);
                  const cls = w.state === "open"
                    ? "border-[#25D366]/40 bg-[#25D366]/10 text-[#128C4A] dark:text-[#25D366]"
                    : w.state === "closed"
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                      : "border-border bg-brema-bg text-muted-foreground";
                  return (
                    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${cls}`} data-testid="wa-window-banner">
                      <Clock className="h-3.5 w-3.5 shrink-0" /> {w.text}
                    </div>
                  );
                })()}
                <Label>Rispondi{active?.channel === "whatsapp" ? " via WhatsApp" : ` da ${(AREA[thread.area] || AREA.sales).label}`}{active?.to_email ? ` · ${active.to_email}` : (active?.from_phone ? ` · ${active.from_phone}` : "")}</Label>
                <Textarea value={replyBody} onChange={(e) => { setReplyBody(e.target.value); if (replyTranslated !== null) setReplyTranslated(null); }} data-testid="inbox-reply-input"
                  className="min-h-[110px]" placeholder={active?.channel === "whatsapp" ? "Scrivi un messaggio WhatsApp (entro 24h dalla risposta del cliente)…" : "Scrivi la tua risposta…"} />
                {replyTranslated !== null && (
                  <div className="rounded-lg border border-brand/30 bg-brand/5 p-3" data-testid="reply-translation-box">
                    <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px]">
                      <span className="inline-flex items-center gap-1 font-semibold text-brand"><Languages className="h-3 w-3" /> Anteprima traduzione in {langLabel(replyLang)} · modificabile, verrà inviata questa versione</span>
                      <button type="button" onClick={() => setReplyTranslated(null)} className="inline-flex items-center gap-0.5 text-muted-foreground hover:text-foreground" data-testid="reply-translation-discard">
                        <X className="h-3 w-3" /> Usa l'originale
                      </button>
                    </div>
                    <Textarea value={replyTranslated} onChange={(e) => setReplyTranslated(e.target.value)} className="min-h-[110px] bg-background" data-testid="reply-translation-input" />
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Select value={replyLang} onValueChange={(v) => { setReplyLang(v); setReplyTranslated(null); }}>
                      <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="reply-lang-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REPLY_LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`reply-lang-${l.code}`}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" onClick={translateReply} disabled={replyTranslating || !replyBody.trim() || replyLang === "it"}
                      className="h-8 rounded-full text-xs" data-testid="reply-translate-btn" title={replyLang === "it" ? "Il destinatario è italiano: nessuna traduzione necessaria" : `Traduci la risposta in ${langLabel(replyLang)}`}>
                      {replyTranslating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Languages className="mr-1.5 h-3.5 w-3.5" />} Traduci
                    </Button>
                    {thread?.suggested_lang && <span className="text-[11px] text-muted-foreground" data-testid="reply-lang-hint">Lingua suggerita: {langLabel(thread.suggested_lang)}</span>}
                  </div>
                  <Button onClick={sendReply} disabled={replySending || !(replyTranslated !== null ? replyTranslated : replyBody).trim()} data-testid="inbox-reply-send-btn"
                    className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                    {replySending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Reply className="mr-2 h-4 w-4" /> {replyTranslated !== null ? "Invia traduzione" : "Invia risposta"}</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual reply logging dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg border-border bg-brema-card" data-testid="inbox-manual-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Registra una risposta ricevuta</DialogTitle>
            <DialogDescription>Inserisci una risposta arrivata nella casella aziendale: verrà associata al contatto e ne aggiornerà lo stato.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email mittente</Label>
                <Input value={manual.from_email} onChange={(e) => setManual((s) => ({ ...s, from_email: e.target.value }))}
                  data-testid="manual-from-email" placeholder="mittente@dominio.it" />
              </div>
              <div className="space-y-1.5">
                <Label>Nome (opzionale)</Label>
                <Input value={manual.from_name} onChange={(e) => setManual((s) => ({ ...s, from_name: e.target.value }))}
                  data-testid="manual-from-name" placeholder="Mario Rossi" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Area</Label>
              <Select value={manual.area} onValueChange={(v) => setManual((s) => ({ ...s, area: v }))}>
                <SelectTrigger data-testid="manual-area-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Commerciale (sales2@)</SelectItem>
                  <SelectItem value="media">Comunicazione (marketing@)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Oggetto</Label>
              <Input value={manual.subject} onChange={(e) => setManual((s) => ({ ...s, subject: e.target.value }))}
                data-testid="manual-subject" placeholder="Re: ..." />
            </div>
            <div className="space-y-1.5">
              <Label>Testo della risposta</Label>
              <Textarea value={manual.body_text} onChange={(e) => setManual((s) => ({ ...s, body_text: e.target.value }))}
                data-testid="manual-body" className="min-h-[120px]" placeholder="Incolla qui il testo della risposta ricevuta…" />
            </div>
            <Button onClick={saveManual} disabled={manualSaving} data-testid="manual-save-btn"
              className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {manualSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Mail className="mr-2 h-4 w-4" /> Registra risposta</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
